/**
 * Executive AI Page
 * 
 * Dedicated page for the Executive AI Co-Pilot.
 * Full-page chat experience with threads sidebar and scope selection on new thread.
 * 
 * STANDALONE MODAL PATTERN:
 * This page uses standalone={true} mode for entity modals (Feature, Epic, Goal, 
 * Persona, Initiative). When AI returns an OPEN_ENTITY action, we fetch the entity
 * data and display the modal in-place, without navigating away from the chat.
 * The modals operate independently - save/delete calls APIs directly and shows
 * toasts without requiring parent state callbacks.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Loader2, Sparkles, MessageSquare, Plus, ChevronDown, Package, Check, Send, Trash2,
  Brain, TrendingUp, AlertTriangle, Users, Target
} from 'lucide-react';
import { 
  executiveCopilotApi, 
  featuresApi, 
  epicsApi, 
  goalsApi, 
  personasApi, 
  initiativesApi,
  productsApi
} from '../services/api';
import { useSafeMode } from '../contexts/SafeModeContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';

// Import chat components
import ExecutiveChat from '../components/dashboard/ExecutiveChat';
import ChatSidebar from '../components/dashboard/ChatSidebar';
import CreateFeatureRequestModal from '../components/dashboard/CreateFeatureRequestModal';

// Import entity detail modals for OPEN_ENTITY action
// These are used in standalone mode - no parent callbacks needed
import FeatureDetailModal from '../components/product/FeatureDetail';
import EpicDetailModal from '../components/epic/EpicDetailModal';
import GoalDetailModal from '../components/goal/GoalDetailModal';
import PersonaDetailModal from '../components/product/PersonaDetailModal';
import InitiativeDetailModal from '../components/initiative/InitiativeDetailModal';
import EntityModalContext from '../contexts/EntityModalContext';


// Scope Selector for New Thread (inline dropdown in chat area)
const NewThreadScopeSelector = ({ scope, scopeLabel, products, onScopeChange }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={cn(
          "inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium",
          "border transition-colors hover:bg-gray-100 dark:hover:bg-gray-800",
          colors.bgSecondary, colors.borderPrimary, colors.textPrimary
        )}>
          {scope === 'all' ? (
            <Globe className="w-4 h-4 text-blue-500" />
          ) : (
            <Package className="w-4 h-4 text-purple-500" />
          )}
          <span>{scopeLabel || 'All Products'}</span>
          <ChevronDown className="w-4 h-4 text-gray-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="w-56">
        <DropdownMenuItem
          onClick={() => onScopeChange('all', 'All Products')}
          className="flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-blue-500" />
            <span>All Products</span>
          </div>
          {scope === 'all' && <Check className="w-4 h-4 text-indigo-600" />}
        </DropdownMenuItem>
        
        {products && products.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5">
              <span className={cn("text-xs font-medium", colors.textMuted)}>
                Products
              </span>
            </div>
            {products.map((product) => (
              <DropdownMenuItem
                key={product.product_id}
                onClick={() => onScopeChange(product.product_id, product.name)}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-purple-500" />
                  <span className="truncate max-w-[160px]">{product.name}</span>
                </div>
                {scope === product.product_id && <Check className="w-4 h-4 text-indigo-600" />}
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Enhanced Welcome State with Scope Selector
const WelcomeStateWithScope = ({ 
  welcomeData, 
  scope, 
  scopeLabel, 
  products, 
  onScopeChange, 
  onSuggestedPrompt,
  onSendMessage,
  isLoading
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [input, setInput] = useState('');

  const handleSend = () => {
    if (!input.trim() || isLoading) return;
    onSendMessage(input.trim());
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto px-4 py-8">
        <div className="flex flex-col items-center max-w-md mx-auto">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 
                          flex items-center justify-center shadow-lg mb-6">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          
          <h2 className={cn("text-xl font-semibold mb-2 text-center", colors.textPrimary)}>
            Executive Intelligence
          </h2>
          
          <p className={cn("text-center mb-8", colors.textSecondary)}>
            {welcomeData?.welcome_message || "Select a product to get started."}
          </p>
          
          {/* Full-width Scope Selector Dropdown - Product only */}
          <div className="w-full mb-6">
            <label className={cn("block text-sm font-medium mb-2", colors.textSecondary)}>
              Which product would you like to explore?
            </label>
            <p className={cn("text-xs mb-3", colors.textMuted)}>
              Select a product to focus the AI on
            </p>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={cn(
                  "flex items-center justify-between w-full px-4 py-3 rounded-lg",
                  "border-2 transition-colors hover:border-indigo-300 dark:hover:border-indigo-700",
                  colors.bgTertiary, colors.borderPrimary, colors.textPrimary
                )}>
                  <div className="flex items-center gap-3">
                    <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", colors.bgSecondary)}>
                      <Package className="w-4 h-4 text-purple-500" />
                    </div>
                    <div className="text-left">
                      <span className="font-medium">{scopeLabel || 'Select a Product'}</span>
                      <p className={cn("text-xs", colors.textMuted)}>
                        {scope ? 'Focus on this product' : 'Choose a product to begin'}
                      </p>
                    </div>
                  </div>
                  <ChevronDown className={cn("w-5 h-5", colors.textMuted)} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-[var(--radix-dropdown-menu-trigger-width)] max-h-80 overflow-y-auto">
                {products && products.length > 0 ? (
                  <>
                    <div className="px-2 py-1.5">
                      <span className={cn("text-xs font-medium", colors.textMuted)}>Select a Product</span>
                    </div>
                    {products.map((product) => (
                      <DropdownMenuItem
                        key={product.product_id}
                        onClick={() => onScopeChange(product.product_id, product.name)}
                        className="flex items-center justify-between py-3"
                      >
                        <div className="flex items-center gap-3">
                          <Package className="w-4 h-4 text-purple-500" />
                          <div>
                            <span className="font-medium truncate max-w-[200px] block">{product.name}</span>
                            <p className={cn("text-xs", colors.textMuted)}>Focus on this product</p>
                          </div>
                        </div>
                        {scope === product.product_id && <Check className="w-4 h-4 text-indigo-600" />}
                      </DropdownMenuItem>
                    ))}
                  </>
                ) : (
                  <div className="px-4 py-3 text-center">
                    <span className={cn("text-sm", colors.textMuted)}>No products available</span>
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          {/* Suggested Prompts */}
          <div className="w-full space-y-2">
            <p className={cn("text-sm font-medium mb-3", colors.textMuted)}>Try asking:</p>
            {(welcomeData?.suggested_prompts || []).map((prompt, idx) => (
              <button
                key={idx}
                onClick={() => onSuggestedPrompt(prompt)}
                className={cn(
                  "w-full text-left px-4 py-3 rounded-lg text-sm transition-colors",
                  "border hover:border-indigo-300 dark:hover:border-indigo-700",
                  colors.bgTertiary, colors.borderPrimary, colors.textSecondary,
                  "hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                )}
              >
                &ldquo;{prompt}&rdquo;
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Fixed Input Area at Bottom - no border, 70% width, 3 rows */}
      <div className="p-4 shrink-0">
        <div className="flex gap-3 max-w-[70%] mx-auto">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything about your products..."
            disabled={isLoading}
            rows={3}
            className={cn(
              "flex-1 resize-none rounded-xl px-4 py-3 text-sm",
              "border focus:outline-none focus:ring-2 focus:ring-indigo-500/50",
              "transition-all",
              colors.bgSecondary, colors.borderPrimary, colors.textPrimary,
              "placeholder:text-gray-400 dark:placeholder:text-gray-500",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          />
          <div className="flex flex-col justify-end">
            <Button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              size="icon"
              className={cn(
                "h-12 w-12 rounded-xl shrink-0",
                "bg-indigo-600 hover:bg-indigo-700 text-white",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
        </div>
        <p className={cn("text-xs mt-2 text-center", colors.textMuted)}>
          Press Enter to send • Shift+Enter for new line
        </p>
      </div>
    </div>
  );
};

const ExecutiveAIPage = () => {
  const navigate = useNavigate();
  const { organization, user } = useAuth();
  const { safeMode, getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [loading, setLoading] = useState(true);
  
  // Scope state (for new threads) - no "all" option, must select a product
  const [scope, setScope] = useState('');
  const [scopeLabel, setScopeLabel] = useState('Select a Product');
  const [products, setProducts] = useState([]);
  
  // Chat state
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [welcomeData, setWelcomeData] = useState(null);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isSessionsLoading, setIsSessionsLoading] = useState(true);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // Clear All state
  const [clearAllDialog, setClearAllDialog] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  
  // Feature Request Modal state
  const [featureRequestModal, setFeatureRequestModal] = useState({ open: false, data: {} });
  
  // ==========================================================================
  // STANDALONE ENTITY MODALS
  // These modals are opened when AI returns an OPEN_ENTITY action.
  // They operate in standalone mode - no parent callbacks needed, just API calls.
  // ==========================================================================
  const [entityModal, setEntityModal] = useState({
    type: null,      // 'feature' | 'epic' | 'goal' | 'persona' | 'initiative'
    data: null,      // The entity data
    productData: null, // Product context for the modal
    loading: false
  });

  // Load products for scope selector
  const loadProducts = useCallback(async () => {
    try {
      const response = await executiveCopilotApi.listProducts();
      const productList = response.data.products || [];
      setProducts(productList);
      
      // Auto-select first product if available and no scope selected
      if (productList.length > 0 && !scope) {
        const firstProduct = productList[0];
        setScope(firstProduct.product_id);
        setScopeLabel(firstProduct.name);
        return firstProduct.product_id;
      }
      return scope;
    } catch (err) {
      console.error('Error loading products:', err);
      return null;
    }
  }, [scope]);

  // Load welcome message - requires a product_id
  const loadWelcome = useCallback(async (productId) => {
    if (!productId || productId === 'all') {
      setWelcomeData(null);
      return;
    }
    try {
      const response = await executiveCopilotApi.getWelcome(productId);
      setWelcomeData(response.data);
    } catch (err) {
      console.error('Error loading welcome:', err);
    }
  }, []);

  // Load sessions
  const loadSessions = useCallback(async () => {
    setIsSessionsLoading(true);
    try {
      const response = await executiveCopilotApi.listSessions();
      // Handle both array response and {sessions: [...]} object response
      const sessionsData = response.data?.sessions || response.data || [];
      setSessions(Array.isArray(sessionsData) ? sessionsData : []);
    } catch (err) {
      console.error('Error loading sessions:', err);
      setSessions([]); // Ensure sessions is always an array on error
    } finally {
      setIsSessionsLoading(false);
    }
  }, []);

  // Load session messages
  const loadSessionMessages = useCallback(async (sessionId) => {
    try {
      const response = await executiveCopilotApi.getSession(sessionId);
      setMessages(response.data.messages || []);
      // Update scope to match the session's scope
      setScope(response.data.scope);
      setScopeLabel(response.data.scope_label);
    } catch (err) {
      console.error('Error loading session:', err);
      toast.error('Failed to load conversation');
    }
  }, []);

  // Initial load
  useEffect(() => {
    const initPage = async () => {
      setLoading(true);
      // Load products first and get initial product_id
      const initialProductId = await loadProducts();
      await Promise.all([
        initialProductId ? loadWelcome(initialProductId) : Promise.resolve(),
        loadSessions(),
      ]);
      setLoading(false);
    };
    initPage();
  }, [loadProducts, loadWelcome, loadSessions]);

  // Handle scope change (only for new threads)
  const handleScopeChange = useCallback(async (newScope, newLabel) => {
    setScope(newScope);
    setScopeLabel(newLabel);
    if (newScope && newScope !== 'all') {
      await loadWelcome(newScope);
    }
  }, [loadWelcome]);

  // Handle send message
  const handleSendMessage = useCallback(async (message) => {
    // Require a product to be selected for new conversations
    if (!activeSession && (!scope || scope === 'all')) {
      toast.error('Please select a product first');
      return;
    }
    
    setIsChatLoading(true);
    
    // Add user message to UI immediately
    const userMsg = { role: 'user', content: message };
    setMessages(prev => [...prev, userMsg]);
    
    try {
      const response = await executiveCopilotApi.chat(message, activeSession?.session_id, scope);
      const data = response.data;
      
      // If this is a new session, update the active session
      if (!activeSession && data.session_id) {
        setActiveSession({ session_id: data.session_id, scope, scope_label: scopeLabel });
        // Reload sessions to show the new one
        loadSessions();
      }
      
      // Add assistant message
      // Handle both 'message' and 'response' keys (unified API uses 'response')
      const assistantMsg = {
        role: 'assistant',
        content: data.message || data.response || '',
        actions: data.actions || [],
      };
      setMessages(prev => [...prev, assistantMsg]);
      
    } catch (err) {
      console.error('Error sending message:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to send message';
      toast.error(errorMsg);
      // Remove the user message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsChatLoading(false);
    }
  }, [activeSession, scope, scopeLabel, loadSessions]);

  // ==========================================================================
  // OPEN ENTITY IN-PLACE
  // Opens entity modals directly on this page instead of navigating away.
  // Fetches entity data and displays the appropriate modal in standalone mode.
  // ==========================================================================
  const openEntityInPlace = useCallback(async (entityType, entityId) => {
    // Special case: Releases navigate to the releases page instead of opening a modal
    if (entityType === 'release') {
      try {
        const resolveRes = await executiveCopilotApi.resolveEntity(entityType, entityId);
        const { product_id } = resolveRes.data;
        navigate(`/products/${product_id}?tab=releases&highlight=${entityId}`);
        return;
      } catch (err) {
        console.error('Error resolving release:', err);
        toast.error(`Could not open release: ${err.response?.data?.detail || 'Not found'}`);
        return;
      }
    }
    
    setEntityModal(prev => ({ ...prev, loading: true }));
    
    try {
      // First resolve to get product_id
      const resolveRes = await executiveCopilotApi.resolveEntity(entityType, entityId);
      const { product_id } = resolveRes.data;
      
      // Fetch product data for modal context
      const productRes = await productsApi.get(product_id);
      const productData = productRes.data;
      
      // Fetch the entity data based on type
      let entityData = null;
      
      switch (entityType) {
        case 'feature':
          const featureRes = await featuresApi.get(entityId);
          entityData = featureRes.data;
          break;
        case 'epic':
          // epicsApi.get returns the epic, getDetails returns more
          const epicRes = await epicsApi.getDetails(entityId);
          entityData = epicRes.data;
          break;
        case 'goal':
          const goalRes = await goalsApi.get(entityId);
          entityData = goalRes.data;
          break;
        case 'persona':
          const personaRes = await personasApi.get(entityId);
          entityData = personaRes.data;
          break;
        case 'initiative':
          const initRes = await initiativesApi.get(entityId);
          entityData = initRes.data;
          break;
        default:
          throw new Error(`Unknown entity type: ${entityType}`);
      }
      
      setEntityModal({
        type: entityType,
        data: entityData,
        productData: productData,
        loading: false
      });
      
    } catch (err) {
      console.error('Error fetching entity:', err);
      toast.error(`Could not open ${entityType}: ${err.response?.data?.detail || 'Not found'}`);
      setEntityModal({ type: null, data: null, productData: null, loading: false });
    }
  }, [navigate]);

  // Close entity modal
  const closeEntityModal = useCallback(() => {
    setEntityModal({ type: null, data: null, productData: null, loading: false });
  }, []);

  // Handle action from chat
  const handleAction = useCallback(async (action) => {
    switch ((action.type || action.action || '').toUpperCase()) {
      case 'OPEN_ENTITY':
        // Open entity modal IN-PLACE instead of navigating away
        // This keeps the user in the Executive AI context
        await openEntityInPlace(action.entity_type, action.entity_id);
        break;
      case 'CREATE_FEATURE_REQUEST':
        // Open the feature request modal with AI-suggested data
        setFeatureRequestModal({
          open: true,
          data: {
            title: action.title || '',
            description: action.description || '',
            priority_level: action.priority_level || 'Medium',
            product_id: action.product_id || '',
            suggested_epic_id: action.suggested_epic_id || '',
          }
        });
        break;
      case 'NAVIGATE':
        navigate(action.path);
        break;
      default:
        console.log('Unknown action:', action);
    }
  }, [navigate, openEntityInPlace]);

  // Handle session selection
  const handleSelectSession = useCallback(async (session) => {
    setActiveSession(session);
    await loadSessionMessages(session.session_id);
  }, [loadSessionMessages]);

  // Handle new chat
  const handleNewChat = useCallback(() => {
    setActiveSession(null);
    setMessages([]);
    // Reset scope to default
    setScope('all');
    setScopeLabel('All Products');
    loadWelcome('all');
  }, [loadWelcome]);

  // Handle rename session
  const handleRenameSession = useCallback(async (sessionId, newTitle) => {
    try {
      await executiveCopilotApi.renameSession(sessionId, newTitle);
      setSessions(prev => prev.map(s => 
        s.session_id === sessionId ? { ...s, title: newTitle } : s
      ));
      toast.success('Conversation renamed');
    } catch (err) {
      console.error('Error renaming session:', err);
      toast.error('Failed to rename conversation');
    }
  }, []);

  // Handle delete session
  const handleDeleteSession = useCallback(async (sessionId) => {
    try {
      await executiveCopilotApi.deleteSession(sessionId);
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      if (activeSession?.session_id === sessionId) {
        setActiveSession(null);
        setMessages([]);
      }
      toast.success('Conversation deleted');
    } catch (err) {
      console.error('Error deleting session:', err);
      toast.error('Failed to delete conversation');
    }
  }, [activeSession]);

  // Handle clear all sessions
  const handleClearAllSessions = useCallback(async () => {
    setIsClearing(true);
    try {
      const response = await executiveCopilotApi.clearAllSessions();
      const deletedCount = response.data?.deleted_count || 0;
      setSessions([]);
      setActiveSession(null);
      setMessages([]);
      setClearAllDialog(false);
      toast.success(`Cleared ${deletedCount} conversation${deletedCount !== 1 ? 's' : ''}`);
    } catch (err) {
      console.error('Error clearing sessions:', err);
      toast.error('Failed to clear conversations');
    } finally {
      setIsClearing(false);
    }
  }, []);

  // Check if we should show the welcome state with scope selector
  const hasMessages = messages && messages.length > 0;
  const hasNoProducts = products.length === 0;

  if (loading) {
    return (
      <div className={cn("flex h-screen", colors.bgPrimary)}>
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className={cn("w-8 h-8 animate-spin mx-auto mb-4", colors.textAccent)} />
            <p className={colors.textSecondary}>Loading Executive AI...</p>
          </div>
        </div>
      </div>
    );
  }

  // Empty state when no products exist
  if (hasNoProducts) {
    return (
      <div className={cn("flex h-screen", colors.bgPrimary)}>
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header title="Executive AI" />
          <main className={cn("flex-1 overflow-y-auto p-6", colors.bgPrimary)}>
            <div className="max-w-7xl mx-auto">
              <div className={cn(
                "rounded-xl border-2 border-dashed p-8 md:p-12",
                colors.borderPrimary
              )}>
                <div className="max-w-3xl mx-auto">
                  {/* Icon */}
                  <div className="flex justify-center mb-6">
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                      <Sparkles className="w-10 h-10 text-white" />
                    </div>
                  </div>
                  
                  {/* Title */}
                  <h2 className={cn("text-2xl font-bold text-center mb-4", colors.textPrimary)}>
                    Your AI Executive Partner
                  </h2>
                  
                  {/* Problem Statement */}
                  <p className={cn("text-center mb-8 leading-relaxed", colors.textSecondary)}>
                    Executive Intelligence transforms your product data into strategic insights. 
                    Once you create a product, you'll have an AI co-pilot that understands your roadmap, 
                    tracks progress, and surfaces what matters most.
                  </p>
                  
                  {/* Feature Grid */}
                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className={cn("p-4 rounded-lg", colors.bgSecondary)}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                          <Brain className="w-4 h-4 text-indigo-600" />
                        </div>
                        <h3 className={cn("font-semibold", colors.textPrimary)}>Strategic Summaries</h3>
                      </div>
                      <p className={cn("text-sm", colors.textMuted)}>
                        Ask for executive briefings on any product. Get instant summaries of progress, blockers, and key decisions needed.
                      </p>
                    </div>
                    
                    <div className={cn("p-4 rounded-lg", colors.bgSecondary)}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                          <AlertTriangle className="w-4 h-4 text-indigo-600" />
                        </div>
                        <h3 className={cn("font-semibold", colors.textPrimary)}>Risk Detection</h3>
                      </div>
                      <p className={cn("text-sm", colors.textMuted)}>
                        Proactively identify at-risk releases, slipping timelines, and scope creep before they become problems.
                      </p>
                    </div>
                    
                    <div className={cn("p-4 rounded-lg", colors.bgSecondary)}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                          <Users className="w-4 h-4 text-indigo-600" />
                        </div>
                        <h3 className={cn("font-semibold", colors.textPrimary)}>Stakeholder Answers</h3>
                      </div>
                      <p className={cn("text-sm", colors.textMuted)}>
                        Prepare for board meetings and stakeholder reviews. Get answers to tough questions about capacity, timelines, and trade-offs.
                      </p>
                    </div>
                    
                    <div className={cn("p-4 rounded-lg", colors.bgSecondary)}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                          <Target className="w-4 h-4 text-indigo-600" />
                        </div>
                        <h3 className={cn("font-semibold", colors.textPrimary)}>Goal Tracking</h3>
                      </div>
                      <p className={cn("text-sm", colors.textMuted)}>
                        Understand how features connect to business objectives. Track OKR progress and identify gaps in your roadmap coverage.
                      </p>
                    </div>
                  </div>
                  
                  {/* CTA */}
                  <div className="text-center">
                    <Button 
                      onClick={() => navigate('/products/new')}
                      size="lg"
                      className="gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                    >
                      <Package className="w-5 h-5" />
                      Create Your First Product
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex h-screen", colors.bgPrimary)}>
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header
          title="Executive AI"
          action={sessions.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setClearAllDialog(true)}
              className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 gap-2"
              data-testid="clear-all-chats-btn"
            >
              <Trash2 className="w-4 h-4" />
              Clear All Chats
            </Button>
          )}
        />
        
        <div className="flex-1 overflow-hidden flex">
          {/* Sidebar */}
          <ChatSidebar
            sessions={sessions}
            activeSessionId={activeSession?.session_id}
            isLoading={isSessionsLoading}
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            onSelectSession={handleSelectSession}
            onNewChat={handleNewChat}
            onRenameSession={handleRenameSession}
            onDeleteSession={handleDeleteSession}
            className={isSidebarCollapsed ? "w-14" : "w-64"}
          />
          
          {/* Main Chat Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {!hasMessages ? (
              <WelcomeStateWithScope
                welcomeData={welcomeData}
                scope={scope}
                scopeLabel={scopeLabel}
                products={products}
                onScopeChange={handleScopeChange}
                onSuggestedPrompt={handleSendMessage}
                onSendMessage={handleSendMessage}
                isLoading={isChatLoading}
              />
            ) : (
              <>
                {/* Chat Header - shows context */}
                <div className={cn(
                  "flex items-center justify-between px-4 py-3 border-b shrink-0",
                  colors.bgCard, colors.borderPrimary
                )}>
                  <div className="flex items-center gap-3">
                    <span className={cn("text-sm font-medium", colors.textSecondary)}>Context</span>
                    <span className={cn(
                      "inline-flex items-center gap-1.5 text-sm px-3 py-1 rounded-full font-medium",
                      "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                    )}>
                      <Package className="w-4 h-4" />
                      {scopeLabel || 'Select a Product'}
                    </span>
                  </div>
                </div>
                
                {/* Messages Area — bridge openEntityInPlace into EntityModalContext
                    so CopilotMarkdown's EntityLinkedText can open modals via API */}
                <EntityModalContext.Provider value={{
                  openEntityModal: openEntityInPlace,
                  selectedEntity: null,
                  closeEntityModal: () => {},
                }}>
                  <ExecutiveChat
                    messages={messages}
                    isLoading={isChatLoading}
                    welcomeData={welcomeData}
                    onSendMessage={handleSendMessage}
                    onAction={handleAction}
                    scope={scope}
                    scopeLabel={scopeLabel}
                    className="flex-1"
                    hideWelcome={true}
                    showHeader={false}
                  />
                </EntityModalContext.Provider>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Clear All Dialog */}
      <Dialog open={clearAllDialog} onOpenChange={setClearAllDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Clear All Conversations</DialogTitle>
            <DialogDescription>
              This will permanently delete all {sessions.length} conversation{sessions.length !== 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>
          <p className={cn("py-4 text-sm", colors.textSecondary)}>
            Are you sure you want to delete all your conversations? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClearAllDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleClearAllSessions}
              disabled={isClearing}
              data-testid="confirm-clear-all-btn"
            >
              {isClearing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Create Feature Request Modal */}
      <CreateFeatureRequestModal
        open={featureRequestModal.open}
        onOpenChange={(open) => setFeatureRequestModal({ open, data: {} })}
        initialData={featureRequestModal.data}
        products={products}
        onSuccess={(feature) => {
          // Optionally add a message to the chat about the created feature
          toast.success(`Feature "${feature.title}" created successfully!`);
        }}
      />
      
      {/* =================================================================== */}
      {/* STANDALONE ENTITY MODALS                                            */}
      {/* These modals open in-place when AI returns OPEN_ENTITY action.      */}
      {/* They use standalone={true} so save/delete operates independently.   */}
      {/* =================================================================== */}
      
      {/* Feature Detail Modal */}
      {entityModal.type === 'feature' && entityModal.data && (
        <FeatureDetailModal
          isOpen={true}
          feature={entityModal.data}
          onClose={closeEntityModal}
          onUpdate={null}
          productContext={entityModal.productData || {}}
          standalone={true}
        />
      )}
      
      {/* Epic Detail Modal */}
      {entityModal.type === 'epic' && entityModal.data && (
        <EpicDetailModal
          isOpen={true}
          epic={entityModal.data}
          onClose={closeEntityModal}
          onUpdate={null}
          productContext={entityModal.productData || {}}
          standalone={true}
        />
      )}
      
      {/* Goal Detail Modal */}
      {entityModal.type === 'goal' && entityModal.data && (
        <GoalDetailModal
          isOpen={true}
          goal={entityModal.data}
          onClose={closeEntityModal}
          onSave={null}
          onDelete={null}
          productContext={entityModal.productData || {}}
          standalone={true}
        />
      )}
      
      {/* Persona Detail Modal */}
      {entityModal.type === 'persona' && entityModal.data && (
        <PersonaDetailModal
          isOpen={true}
          persona={entityModal.data}
          onClose={closeEntityModal}
          onUpdate={null}
          productContext={entityModal.productData || {}}
          standalone={true}
        />
      )}
      
      {/* Initiative Detail Modal */}
      {entityModal.type === 'initiative' && entityModal.data && (
        <InitiativeDetailModal
          isOpen={true}
          initiative={entityModal.data}
          onClose={closeEntityModal}
          onSave={null}
          onDelete={null}
          productContext={entityModal.productData || {}}
          standalone={true}
        />
      )}
      
      {/* Loading indicator when fetching entity */}
      {entityModal.loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className={cn("p-6 rounded-lg", colors.bgCard)}>
            <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mx-auto" />
            <p className={cn("mt-2 text-sm", colors.textSecondary)}>Loading...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExecutiveAIPage;
